import {
  Box,
  Button,
  Container,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import heroDesktop from "../../assets/images/hero-bg-desktop.png";
import heroMobile from "../../assets/images/hero-bg-mobile.png";

const Hero = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const scrollToEstimator = () => {
    document
      .getElementById("estimator")
      ?.scrollIntoView({ behavior: "smooth" });
  };

  const talkToExpert = () => {
    const phone = "919532119007"; // no + sign
    const message = "Hello Costlix, I want to talk to an cost expert.";
    const encodedMessage = encodeURIComponent(message);

    window.open(`https://wa.me/${phone}?text=${encodedMessage}`, "_blank");
  };

  return (
    // <Box
    //   id="hero"
    //   component="section"
    //   sx={{
    //     position: "relative",

    //     /* 👇 Hero background continues behind header */
    //     // mt: { xs: "-64px", md: "-76px" }, // header height
    //     pt: { xs: "calc(64px + 4rem)", md: "calc(76px + 6rem)" },

    //     pb: { xs: 7, md: 14 },

    //     backgroundImage: {
    //       xs: `url(${heroMobile})`,
    //       md: `url(${heroDesktop})`,
    //     },
    //     backgroundSize: "cover",
    //     backgroundPosition: {
    //       xs: "center top",
    //       md: "center",
    //     },
    //     backgroundRepeat: "no-repeat",
    //     minHeight: {
    //       xs: "auto", // ❗ must be auto on mobile
    //       md: "100vh",
    //     },
    //   }}
    // >
    <Box
      id="hero"
      component="section"
      sx={{
        position: "relative",

        /* space for fixed header */
        pt: { xs: "64px", md: "76px" },

        minHeight: "100svh", // ✅ SAFE viewport height
        display: "flex",
        alignItems: "center",

        backgroundImage: {
          xs: `url(${heroMobile})`,
          md: `url(${heroDesktop})`,
        },
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
      }}
    >
      <Container maxWidth="md">
        <Stack
          spacing={{ xs: 3, md: 4 }}
          alignItems={{ xs: "flex-start", md: "center" }}
          textAlign={{ xs: "left", md: "center" }}
        >
          {/* Eyebrow */}
          <Typography
            sx={{
              fontSize: "0.75rem",
              fontWeight: 600,
              letterSpacing: "0.12em",
              textTransform: "uppercase",
              color: "var(--color-midnight-70)",
            }}
          >
            Project Cost Estimation Tool
          </Typography>

          {/* H1 */}
          <Typography
            component="h1"
            sx={{
              fontSize: { xs: "1.85rem", md: "2.9rem" },
              fontWeight: 700,
              lineHeight: 1.15,
              maxWidth: 760,
              color: "var(--color-midnight)",
            }}
          >
            Estimate Your{" "}
            <Box component="span" sx={{ color: "var(--color-fiery)" }}>
              Website & App Development Cost
            </Box>{" "}
            Instantly
          </Typography>

          {/* Description */}
          <Typography
            sx={{
              fontSize: { xs: "0.95rem", md: "1.05rem" },
              maxWidth: 620,
              color: "var(--color-midnight-70)",
            }}
          >
            Get a clear, realistic development cost based on features, scope,
            and complexity — not guesses.
          </Typography>

          {/* Trust pills */}
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1.2}
            sx={{ mt: 1 }}
          >
            {[
              "✓ No signup required",
              "✓ Instant estimate",
              "✓ Expert-reviewed pricing",
            ].map((item) => (
              <Box
                key={item}
                sx={{
                  px: 1.6,
                  py: 0.6,
                  borderRadius: 999,
                  fontSize: "0.85rem",
                  fontWeight: 600,
                  backgroundColor: "var(--color-midnight-15)",
                  color: "var(--color-midnight)",
                }}
              >
                {item}
              </Box>
            ))}
          </Stack>

          {/* CTAs */}
          <Stack
            direction="row" // ✅ always same line
            spacing={2}
            justifyContent="center"
            sx={{
              mt: { xs: 3, md: 4 },
              width: "100%",
            }}
          >
            {/* Primary CTA */}
            <Button
              variant="contained"
              color="secondary"
              onClick={scrollToEstimator}
              sx={{
                flex: 1,
                maxWidth: { xs: "100%", sm: 260 },

                height: { xs: 46, sm: 52 }, // ✅ smaller on mobile
                px: { xs: 2, sm: 3 },
                py: { xs: 1, sm: 1.4 },

                borderRadius: "14px",
                fontSize: { xs: "0.85rem", sm: "0.95rem" }, // ✅ key fix
                fontWeight: 600,

                boxShadow: {
                  xs: "0 6px 14px rgba(233,79,55,0.22)",
                  sm: "0 10px 24px rgba(233,79,55,0.25)",
                },

                transition: "all 0.25s ease",
                "&:hover": {
                  backgroundColor: "var(--color-fiery-hover)",
                  boxShadow: "0 14px 30px rgba(233,79,55,0.35)",
                  transform: "translateY(-2px)",
                },
              }}
            >
              {isMobile ? "Calculate Cost" : "Calculate Project Cost"}
            </Button>

            <Button
              variant="outlined"
              onClick={talkToExpert}
              sx={{
                flex: 1,
                maxWidth: { xs: "100%", sm: 260 },

                height: { xs: 46, sm: 52 },
                px: { xs: 2, sm: 3 },
                py: { xs: 1, sm: 1.4 },

                borderRadius: "14px",
                fontSize: { xs: "0.85rem", sm: "0.95rem" },
                fontWeight: 600,

                borderColor: "var(--color-midnight)",
                color: "var(--color-midnight)",

                transition: "all 0.25s ease",
                "&:hover": {
                  backgroundColor: "rgba(57,62,65,0.05)",
                  transform: "translateY(-1px)",
                },
              }}
            >
              {isMobile ? "Cost Expert" : "Talk to a Cost Expert"}
            </Button>
          </Stack>
          <Typography
            sx={{
              mt: 1.4,
              fontSize: { xs: "0.75rem", sm: "0.8rem" },
              color: "var(--color-midnight-70)",
              textAlign: { xs: "left", md: "center" },
              fontWeight: 500,
            }}
          >
            Instant cost estimate • Designed for real project planning
          </Typography>
        </Stack>
      </Container>
    </Box>
  );
};

export default Hero;
